<template>
  <div class="p-6 space-y-6">
    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h1 class="page-title">行銷郵件追蹤報表</h1>
        <p class="page-subtitle">查看所有行銷活動的開信率、點擊率與退訂資訊</p>
      </div>
      <div class="flex items-center gap-2">
        <button class="btn btn-secondary btn-sm" @click="exportMarketingReportPdf" :disabled="marketingReportPdfLoading">
          {{ marketingReportPdfLoading ? '產生中...' : '匯出 PDF' }}
        </button>
        <button class="btn btn-primary" @click="activeTab === 'ab' ? loadAbCampaigns() : loadAll()" :disabled="loading || abLoading">
          <svg v-if="loading" class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
          </svg>
          <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
          </svg>
          重新整理
        </button>
      </div>
    </div>

    <!-- Tabs -->
    <div class="flex gap-1" style="border-bottom:1px solid var(--border);">
      <button class="tab-item" :class="activeTab === 'overview' ? 'active' : ''" @click="activeTab = 'overview'">概覽報表</button>
      <button class="tab-item" :class="activeTab === 'ab' ? 'active' : ''" @click="activeTab = 'ab'; loadAbCampaigns()">A/B 測試比較</button>
      <button class="tab-item" :class="activeTab === 'scheduled' ? 'active' : ''" @click="activeTab = 'scheduled'; loadScheduled()">
        排程發送
        <span v-if="scheduledCampaigns.length" class="badge badge-warning" style="margin-left:5px;">{{ scheduledCampaigns.length }}</span>
      </button>
      <button class="tab-item" :class="activeTab === 'journey' ? 'active' : ''" @click="activeTab = 'journey'; loadJourneyStats()">
        旅程統計
        <span v-if="journeys.length" class="badge badge-default" style="margin-left:5px;">{{ journeys.length }}</span>
      </button>
      <button class="tab-item" :class="activeTab === 'tracking' ? 'active' : ''" @click="activeTab = 'tracking'; loadTrackingEvents()">
        追蹤報告
      </button>
      <button class="tab-item" :class="activeTab === 'list_health' ? 'active' : ''" @click="activeTab = 'list_health'">
        名單健康度
      </button>
      <button class="tab-item" :class="activeTab === 'unsub_analysis' ? 'active' : ''" @click="activeTab = 'unsub_analysis'">
        退訂分析
      </button>
      <button class="tab-item" :class="activeTab === 'replies' ? 'active' : ''" @click="activeTab = 'replies'">
        回覆分類
      </button>
      <button class="tab-item" :class="activeTab === 'competitor' ? 'active' : ''" @click="activeTab = 'competitor'">
        競爭者分析
      </button>
      <button class="tab-item" :class="activeTab === 'monthly_report' ? 'active' : ''" @click="activeTab = 'monthly_report'">
        月度報告
      </button>
    </div>

    <!-- Tracking Report Tab -->
    <template v-if="activeTab === 'tracking'">
      <!-- Filters -->
      <div class="card p-4 flex flex-wrap gap-3 items-end">
        <div>
          <label class="block text-xs font-medium mb-1" style="color:var(--gray-600);">活動篩選</label>
          <select v-model="trackingFilter.campaign_id" class="input" style="min-width:180px;">
            <option value="">全部活動</option>
            <option v-for="c in trackingCampaigns" :key="c.id" :value="c.id">
              #{{ c.id }} {{ c.subject || '（無主旨）' }}
            </option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium mb-1" style="color:var(--gray-600);">事件類型</label>
          <select v-model="trackingFilter.event_type" class="input" style="min-width:120px;">
            <option value="">全部</option>
            <option value="open">開信</option>
            <option value="click">點擊</option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium mb-1" style="color:var(--gray-600);">收件人</label>
          <input v-model="trackingFilter.contact_email" type="email" class="input" placeholder="email@example.com" style="min-width:200px;" />
        </div>
        <div>
          <label class="block text-xs font-medium mb-1" style="color:var(--gray-600);">起始時間</label>
          <input v-model="trackingFilter.since" type="date" class="input" />
        </div>
        <button class="btn btn-primary btn-sm" @click="loadTrackingEvents" :disabled="trackingLoading">
          {{ trackingLoading ? '載入中...' : '查詢' }}
        </button>
      </div>

      <!-- Summary Cards -->
      <div v-if="trackingEvents.length" class="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div class="card p-4 text-center">
          <div class="text-2xl font-bold" style="color:var(--brand-600,#2563eb);">{{ trackingSummary.opens }}</div>
          <div class="text-xs mt-1" style="color:var(--gray-500);">開信次數</div>
        </div>
        <div class="card p-4 text-center">
          <div class="text-2xl font-bold" style="color:var(--color-success);">{{ trackingSummary.clicks }}</div>
          <div class="text-xs mt-1" style="color:var(--gray-500);">點擊次數</div>
        </div>
        <div class="card p-4 text-center">
          <div class="text-2xl font-bold" style="color:var(--gray-700);">{{ trackingSummary.uniqueRecipients }}</div>
          <div class="text-xs mt-1" style="color:var(--gray-500);">不重複收件人</div>
        </div>
        <div class="card p-4 text-center">
          <div class="text-2xl font-bold" style="color:var(--color-warning);">{{ trackingSummary.formSubmissions }}</div>
          <div class="text-xs mt-1" style="color:var(--gray-500);">表單提交</div>
        </div>
      </div>

      <!-- Events Table -->
      <div class="card p-0 overflow-hidden">
        <div v-if="trackingLoading" class="p-10 text-center" style="color:var(--gray-400);">載入中...</div>
        <div v-else-if="!trackingEvents.length" class="p-10 text-center" style="color:var(--gray-400);">
          尚無追蹤記錄。請選擇活動或調整篩選條件後查詢。
        </div>
        <div v-else class="overflow-x-auto">
          <table class="data-table">
            <thead>
              <tr>
                <th class="px-4 py-3" style="width:160px;">時間</th>
                <th class="px-4 py-3" style="width:100px;">事件</th>
                <th class="px-4 py-3">收件人</th>
                <th class="px-4 py-3" style="width:120px;">活動</th>
                <th class="px-4 py-3">連結</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(ev, idx) in filteredTrackingEvents" :key="idx" class="border-t" style="border-color:var(--border);">
                <td class="px-4 py-2 whitespace-nowrap text-xs" style="color:var(--gray-500);">{{ fmtDateTime(ev.at) }}</td>
                <td class="px-4 py-2 whitespace-nowrap">
                  <span class="badge" :class="ev.type === 'open' ? 'badge-info' : (ev.type === 'click' ? 'badge-success' : 'badge-default')">
                    {{ ev.type === 'open' ? '開信' : (ev.type === 'click' ? '點擊' : (ev.type === 'form_submission' ? '表單' : ev.type)) }}
                  </span>
                </td>
                <td class="px-4 py-2 text-sm" style="color:var(--gray-700);">{{ ev.recipient || ev.email || '—' }}</td>
                <td class="px-4 py-2 text-xs" style="color:var(--gray-600);">#{{ ev.campaign_id || '—' }}</td>
                <td class="px-4 py-2 text-xs" style="color:var(--brand-500);max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">
                  <a v-if="ev.url" :href="ev.url" target="_blank" rel="noopener noreferrer" style="color:var(--brand-500);">{{ ev.url }}</a>
                  <span v-else style="color:var(--gray-400);">—</span>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="trackingEvents.length >= 200" class="px-4 py-3 text-xs text-center" style="color:var(--gray-400);">
            顯示最近 200 筆記錄。如需更多請縮小篩選範圍。
          </div>
        </div>
      </div>
    </template>

    <!-- A/B Test Tab -->
    <template v-if="activeTab === 'ab'">
      <div v-if="abLoading" class="card p-10 text-center" style="color:var(--gray-400);">載入中…</div>
      <div v-else-if="!abCampaigns.length" class="card p-10 text-center" style="color:var(--gray-400);">尚無 A/B 測試活動</div>
      <template v-else>
        <div v-for="group in abCampaigns" :key="group.id" class="card p-5 space-y-4">

          <!-- Group Header -->
          <div class="flex items-start justify-between">
            <div>
              <div class="font-semibold" style="font-size:1rem;">{{ group.subject }}</div>
              <div class="text-sm mt-0.5" style="color:var(--gray-400);">
                活動 #{{ group.id }} · {{ fmtDate(group.created_at) }}
                <template v-if="group.ab?.sample_ratio"> · 抽樣 {{ Math.round((group.ab.sample_ratio || 0) * 100) }}%</template>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <span v-if="group.ab?.winner" class="badge badge-success">
                勝者：變體 {{ group.ab.winner.ab_variant }}
              </span>
              <span v-else-if="group.status === 'ab_testing'" class="badge badge-warning">測試進行中</span>
              <span v-else class="badge" style="background:var(--gray-100);color:var(--gray-600);">{{ statusLabel(group.status) }}</span>
            </div>
          </div>

          <!-- Metric + ETA row -->
          <div class="flex flex-wrap gap-4 text-sm" style="color:var(--gray-500);">
            <span>評比指標：<strong style="color:var(--gray-700);">{{ metricLabel(group.ab?.metric) }}</strong></span>
            <template v-if="group.ab?.wait_minutes && !group.ab?.decided_at">
              <span>等待 <strong style="color:var(--gray-700);">{{ group.ab.wait_minutes }}</strong> 分鐘後自動決定</span>
            </template>
            <template v-if="group.ab?.auto_decide_eta">
              <span style="color:var(--color-warning);">
                ⏱ 預計自動決定：{{ fmtDateTime(group.ab.auto_decide_eta) }}
              </span>
            </template>
            <template v-if="group.ab?.decided_at">
              <span style="color:var(--color-success);">決定於 {{ fmtDateTime(group.ab.decided_at) }}</span>
            </template>
            <template v-if="group.ab?.tie_breaker">
              <span>平局優先：<strong style="color:var(--gray-700);">變體 {{ group.ab.tie_breaker }}</strong></span>
            </template>
          </div>

          <!-- Bar Chart Comparison (A vs B) -->
          <div class="grid gap-4" style="grid-template-columns: 1fr 1fr;">
            <!-- Variant A -->
            <div class="rounded-lg p-4"
              :style="{
                background: group.ab?.winner?.ab_variant === 'A' ? 'var(--color-success-bg)' : 'var(--surface-secondary,var(--gray-50))',
                border: '1px solid ' + (group.ab?.winner?.ab_variant === 'A' ? 'var(--color-success)' : 'var(--border)')
              }">
              <div class="flex items-center gap-2 mb-3">
                <span class="badge" :class="group.ab?.winner?.ab_variant === 'A' ? 'badge-success' : ''">變體 A</span>
                <span v-if="group.ab?.winner?.ab_variant === 'A'" class="text-xs font-bold" style="color:var(--color-success);">🏆 勝出</span>
                <span v-else-if="group.ab?.leader_variant === 'A' && !group.ab?.winner" class="text-xs" style="color:var(--color-warning);">▲ 領先</span>
              </div>
              <div class="text-2xl font-bold mb-1"
                :style="{ color: group.ab?.winner?.ab_variant === 'A' ? 'var(--color-success)' : 'var(--gray-700)' }">
                {{ group.ab ? pct(group.ab.a_metric_pct / 100) : '—' }}
              </div>
              <div class="text-xs mb-3" style="color:var(--gray-500);">{{ metricLabel(group.ab?.metric) }}</div>
              <div class="rounded-full overflow-hidden" style="height:8px;background:var(--gray-200);">
                <div class="h-full rounded-full transition-all"
                  :style="{ width: abBarWidth(group, 'A') + '%', background: group.ab?.winner?.ab_variant === 'A' ? 'var(--color-success)' : 'var(--brand-400)' }">
                </div>
              </div>
              <!-- Sub-stats -->
              <template v-if="group.ab?.variants">
                <div v-for="v in group.ab.variants.filter(x => x.ab_variant === 'A')" :key="v.id" class="mt-3 space-y-1" style="font-size:0.75rem;color:var(--gray-500);">
                  <div class="truncate" :title="v.subject">主旨：{{ v.subject }}</div>
                  <div class="grid gap-x-3" style="grid-template-columns:1fr 1fr 1fr;margin-top:0.5rem;">
                    <div class="text-center p-1 rounded" style="background:var(--gray-100);">
                      <div class="font-bold" style="color:var(--gray-700);font-size:0.9rem;">{{ v.total_recipients || 0 }}</div>
                      <div style="font-size:0.65rem;">寄出</div>
                    </div>
                    <div class="text-center p-1 rounded" style="background:var(--gray-100);">
                      <div class="font-bold" style="color:var(--brand-600);font-size:0.9rem;">{{ (v.open_rate ?? 0).toFixed(1) }}%</div>
                      <div style="font-size:0.65rem;">開信率</div>
                    </div>
                    <div class="text-center p-1 rounded" style="background:var(--gray-100);">
                      <div class="font-bold" style="color:var(--brand-500);font-size:0.9rem;">{{ (v.click_rate ?? 0).toFixed(1) }}%</div>
                      <div style="font-size:0.65rem;">點擊率</div>
                    </div>
                  </div>
                </div>
              </template>
            </div>

            <!-- Variant B -->
            <div class="rounded-lg p-4"
              :style="{
                background: group.ab?.winner?.ab_variant === 'B' ? 'var(--color-success-bg)' : 'var(--surface-secondary,var(--gray-50))',
                border: '1px solid ' + (group.ab?.winner?.ab_variant === 'B' ? 'var(--color-success)' : 'var(--border)')
              }">
              <div class="flex items-center gap-2 mb-3">
                <span class="badge" :class="group.ab?.winner?.ab_variant === 'B' ? 'badge-success' : ''">變體 B</span>
                <span v-if="group.ab?.winner?.ab_variant === 'B'" class="text-xs font-bold" style="color:var(--color-success);">🏆 勝出</span>
                <span v-else-if="group.ab?.leader_variant === 'B' && !group.ab?.winner" class="text-xs" style="color:var(--color-warning);">▲ 領先</span>
              </div>
              <div class="text-2xl font-bold mb-1"
                :style="{ color: group.ab?.winner?.ab_variant === 'B' ? 'var(--color-success)' : 'var(--gray-700)' }">
                {{ group.ab ? pct(group.ab.b_metric_pct / 100) : '—' }}
              </div>
              <div class="text-xs mb-3" style="color:var(--gray-500);">{{ metricLabel(group.ab?.metric) }}</div>
              <div class="rounded-full overflow-hidden" style="height:8px;background:var(--gray-200);">
                <div class="h-full rounded-full transition-all"
                  :style="{ width: abBarWidth(group, 'B') + '%', background: group.ab?.winner?.ab_variant === 'B' ? 'var(--color-success)' : 'var(--gray-400)' }">
                </div>
              </div>
              <template v-if="group.ab?.variants">
                <div v-for="v in group.ab.variants.filter(x => x.ab_variant === 'B')" :key="v.id" class="mt-3 space-y-1" style="font-size:0.75rem;color:var(--gray-500);">
                  <div class="truncate" :title="v.subject">主旨：{{ v.subject }}</div>
                  <div class="grid gap-x-3" style="grid-template-columns:1fr 1fr 1fr;margin-top:0.5rem;">
                    <div class="text-center p-1 rounded" style="background:var(--gray-100);">
                      <div class="font-bold" style="color:var(--gray-700);font-size:0.9rem;">{{ v.total_recipients || 0 }}</div>
                      <div style="font-size:0.65rem;">寄出</div>
                    </div>
                    <div class="text-center p-1 rounded" style="background:var(--gray-100);">
                      <div class="font-bold" style="color:var(--brand-600);font-size:0.9rem;">{{ (v.open_rate ?? 0).toFixed(1) }}%</div>
                      <div style="font-size:0.65rem;">開信率</div>
                    </div>
                    <div class="text-center p-1 rounded" style="background:var(--gray-100);">
                      <div class="font-bold" style="color:var(--brand-500);font-size:0.9rem;">{{ (v.click_rate ?? 0).toFixed(1) }}%</div>
                      <div style="font-size:0.65rem;">點擊率</div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>

          <!-- Winner announced panel -->
          <div v-if="group.ab?.winner" class="p-4 rounded-lg flex items-center gap-3"
            style="background:var(--color-success-bg);border:1px solid var(--color-success);">
            <div class="text-2xl">🏆</div>
            <div>
              <div class="font-semibold" style="color:var(--color-success);font-size:0.9rem;">
                變體 {{ group.ab.winner.ab_variant }} 勝出 · {{ metricLabel(group.ab.metric) }} {{ (group.ab.winner.ab_variant === 'A' ? group.ab.a_metric_pct : group.ab.b_metric_pct).toFixed(1) }}%
              </div>
              <div class="text-xs mt-0.5" style="color:var(--gray-600);">
                {{ group.ab.winner.subject }}
                <template v-if="group.ab.remainder_moved_count > 0">
                  · 剩餘 {{ group.ab.remainder_moved_count }} 封已寄給未入樣收件人
                </template>
              </div>
            </div>
          </div>

          <!-- Remainder pending warning -->
          <div v-if="group.ab?.remainder_pending_count > 0 && !group.ab?.winner"
            class="text-sm p-3 rounded-lg"
            style="background:var(--color-warning-bg);color:var(--color-warning);border:1px solid var(--color-warning);">
            ⏳ 尚有 {{ group.ab.remainder_pending_count }} 封剩餘郵件，等待決出勝者後發送
          </div>

          <!-- Force-decide action panel (only when ab_testing and no winner yet) -->
          <div v-if="group.status === 'ab_testing' && !group.ab?.winner"
            class="p-4 rounded-lg space-y-3"
            style="background:var(--surface-secondary,var(--gray-50));border:1px solid var(--border);">
            <div class="text-sm font-semibold" style="color:var(--gray-700);">決策管理</div>
            <div class="flex flex-wrap gap-2">
              <button class="btn btn-sm"
                style="background:var(--brand-500);color:#fff;border:none;"
                :disabled="abDeciding[group.id]?.loading"
                @click="forceDecideAb(group.id, 'A')">
                {{ abDeciding[group.id]?.variant === 'A' && abDeciding[group.id]?.loading ? '處理中…' : '強制 A 勝出' }}
              </button>
              <button class="btn btn-sm"
                style="background:var(--brand-400);color:#fff;border:none;"
                :disabled="abDeciding[group.id]?.loading"
                @click="forceDecideAb(group.id, 'B')">
                {{ abDeciding[group.id]?.variant === 'B' && abDeciding[group.id]?.loading ? '處理中…' : '強制 B 勝出' }}
              </button>
              <button class="btn btn-secondary btn-sm"
                :disabled="abDeciding[group.id]?.loading"
                @click="forceDecideAb(group.id, '')">
                {{ abDeciding[group.id]?.variant === '' && abDeciding[group.id]?.loading ? '處理中…' : '立即自動決定' }}
              </button>
            </div>
            <div class="text-xs" style="color:var(--gray-500);">
              強制決定後，勝出方案將立即發送給剩餘未入樣的收件人。
            </div>
          </div>

          <!-- AI Generate B Variant -->
          <div class="p-4 rounded-lg space-y-3" style="background:linear-gradient(135deg,#eef2ff,#f5f3ff);border:1px solid #c7d2fe;">
            <div class="flex items-center gap-2">
              <span style="font-size:1.2rem;">🤖</span>
              <div class="text-sm font-semibold" style="color:#4f46e5;">AI 自動生成 B 版本</div>
            </div>
            <div class="flex flex-wrap gap-2 items-center">
              <select v-model="abVariantStyle[group.id]" class="input input-sm" style="max-width:160px;font-size:0.8rem;">
                <option value="engaging">更吸引人（強 CTA）</option>
                <option value="formal">更正式（B2B）</option>
                <option value="concise">更簡短精煉</option>
                <option value="emotional">情感化故事性</option>
              </select>
              <button class="btn btn-sm" style="background:#4f46e5;color:#fff;border:none;"
                :disabled="abVariantGenerating[group.id]"
                @click="generateAbVariant(group)">
                {{ abVariantGenerating[group.id] ? 'AI 生成中...' : '生成 B 版內容' }}
              </button>
            </div>
            <div v-if="abVariantResult[group.id]" class="rounded-lg p-3 space-y-2" style="background:#fff;border:1px solid #c7d2fe;">
              <div class="text-xs font-bold" style="color:#4f46e5;">✅ B 版主旨建議</div>
              <div class="text-sm font-medium" style="color:var(--gray-800);">{{ abVariantResult[group.id].subject }}</div>
              <div class="text-xs" style="color:var(--gray-500);">內文已產生，可複製後使用於新 A/B 測試活動的 B 版本。</div>
              <div class="flex gap-2">
                <button class="btn btn-xs btn-outline" @click="copyAbVariantSubject(group.id)">複製主旨</button>
                <button class="btn btn-xs btn-outline" @click="copyAbVariantBody(group.id)">複製 HTML</button>
              </div>
            </div>
            <div v-if="abVariantError[group.id]" class="text-xs" style="color:var(--color-danger);">{{ abVariantError[group.id] }}</div>
          </div>

        </div>
      </template>
    </template>

    <!-- Overview Tab -->
    <template v-if="activeTab === 'overview'">

    <!-- Gmail tracking notice -->
    <div class="flex items-start gap-2 px-4 py-2.5 rounded-lg text-xs" style="background:#fefce8;border:1px solid #fde047;color:#854d0e;">
      <svg class="w-3.5 h-3.5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"/></svg>
      <span>
        <strong>開信追蹤限制：</strong>Gmail / Apple Mail 等郵件客戶端會透過自家代理伺服器快取圖片，導致追蹤像素可能無法在使用者真正開信時再次觸發。
        <strong>點閱率為更可靠的互動指標。</strong>
        傳送後 10 秒內的預取請求已自動過濾。
      </span>
    </div>

    <!-- Summary Cards -->
    <div class="grid grid-cols-2 gap-4" style="grid-template-columns: repeat(4, 1fr);">
      <div class="card p-5 text-center">
        <div class="text-2xl font-bold" style="color:var(--brand-600);">{{ summary.totalCampaigns }}</div>
        <div class="text-sm mt-1" style="color:var(--gray-500);">活動總數</div>
      </div>
      <div class="card p-5 text-center">
        <div class="text-2xl font-bold" style="color:var(--brand-600);">{{ summary.totalSent.toLocaleString() }}</div>
        <div class="text-sm mt-1" style="color:var(--gray-500);">總寄出數</div>
      </div>
      <div class="card p-5 text-center">
        <div class="text-2xl font-bold" :style="{ color: rateColor(summary.avgOpenRate) }">{{ pct(summary.avgOpenRate) }}</div>
        <div class="text-xs mt-0.5" style="color:var(--gray-500);">開信率</div>
        <div class="text-xs mt-0.5" style="color:var(--gray-400);">Gmail 用戶可能不準確</div>
      </div>
      <div class="card p-5 text-center">
        <div class="text-2xl font-bold" :style="{ color: rateColor(summary.avgClickRate) }">{{ pct(summary.avgClickRate) }}</div>
        <div class="text-xs mt-0.5" style="color:var(--gray-500);">點閱率</div>
        <div class="text-xs mt-0.5" style="color:var(--color-success);font-weight:500;">✓ 可靠指標</div>
      </div>
    </div>

    <!-- Filter Bar -->
    <div class="card p-4 flex gap-3 items-center flex-wrap">
      <input v-model="filterQ" class="input" style="max-width:240px;" placeholder="搜尋主旨或 ID…">
      <select v-model="filterStatus" class="input" style="max-width:160px;">
        <option value="">全部狀態</option>
        <option value="sent">已完成</option>
        <option value="sending">寄送中</option>
        <option value="failed">失敗</option>
        <option value="ab_testing">A/B 測試中</option>
      </select>
      <div class="ml-auto flex items-center gap-2">
        <span class="text-sm" style="color:var(--gray-500);">共 {{ filteredCampaigns.length }} 筆</span>
        <button class="btn btn-secondary btn-sm" @click="exportOverviewCsv" :disabled="!filteredCampaigns.length" title="匯出目前篩選結果為 CSV">
          ↓ 匯出 CSV
        </button>
      </div>
    </div>

    <!-- Campaign Table -->
    <div class="card overflow-hidden">
      <table class="data-table">
        <thead>
          <tr>
            <th style="width:40px;">ID</th>
            <th>活動主旨</th>
            <th style="width:80px;text-align:center;">狀態</th>
            <th style="width:80px;text-align:right;">寄出</th>
            <th style="width:200px;">開信率</th>
            <th style="width:200px;">點擊率</th>
            <th style="width:80px;text-align:right;">退訂</th>
            <th style="width:120px;">建立時間</th>
            <th style="width:60px;"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading && !campaigns.length">
            <td colspan="9" class="text-center py-10" style="color:var(--gray-400);">載入中…</td>
          </tr>
          <tr v-else-if="!filteredCampaigns.length">
            <td colspan="9" class="text-center py-10" style="color:var(--gray-400);">尚無資料</td>
          </tr>
          <template v-for="c in filteredCampaigns" :key="c.id">
            <tr class="cursor-pointer" @click="openDetail(c)">
              <td class="text-xs" style="color:var(--gray-400);">#{{ c.id }}</td>
              <td>
                <div class="font-medium truncate" style="max-width:280px;">{{ c.subject }}</div>
                <div v-if="c.ab_variant" class="text-xs mt-0.5" style="color:var(--gray-400);">A/B 變體 {{ c.ab_variant }}</div>
              </td>
              <td class="text-center">
                <span class="badge" :class="statusBadge(c.status)">{{ statusLabel(c.status) }}</span>
              </td>
              <td class="text-right text-sm">{{ (c.total_recipients || 0).toLocaleString() }}</td>
              <td class="cursor-pointer select-none" style="user-select:none;"
                @click.stop="toggleExpand(c, 'open')"
                :title="expandedRows[c.id]?.open && expandedRows[c.id]?.tab === 'open' ? '收合開信名單' : `展開 ${c.open_count||0} 筆開信記錄`">
                <div class="flex items-center gap-1.5">
                  <div class="flex-1 rounded-full overflow-hidden" style="height:6px;background:var(--gray-100);">
                    <div class="h-full rounded-full" :style="{ width: openRatePct(c) + '%', background: 'var(--color-success)' }"></div>
                  </div>
                  <span class="text-xs w-12 text-right font-medium" :style="{ color: rateColor(openRate(c)) }">{{ pct(openRate(c)) }}</span>
                  <svg class="w-3 h-3 flex-shrink-0 transition-transform duration-150"
                    :style="{ color: 'var(--gray-400)', transform: (expandedRows[c.id]?.open && expandedRows[c.id]?.tab === 'open') ? 'rotate(180deg)' : 'rotate(0deg)' }"
                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"/>
                  </svg>
                </div>
              </td>
              <td class="cursor-pointer select-none" style="user-select:none;"
                @click.stop="toggleExpand(c, 'click')"
                :title="expandedRows[c.id]?.open && expandedRows[c.id]?.tab === 'click' ? '收合點擊名單' : `展開 ${c.click_count||0} 筆點擊記錄`">
                <div class="flex items-center gap-1.5">
                  <div class="flex-1 rounded-full overflow-hidden" style="height:6px;background:var(--gray-100);">
                    <div class="h-full rounded-full" :style="{ width: clickRatePct(c) + '%', background: 'var(--brand-500)' }"></div>
                  </div>
                  <span class="text-xs w-12 text-right font-medium" :style="{ color: rateColor(clickRate(c)) }">{{ pct(clickRate(c)) }}</span>
                  <svg class="w-3 h-3 flex-shrink-0 transition-transform duration-150"
                    :style="{ color: 'var(--gray-400)', transform: (expandedRows[c.id]?.open && expandedRows[c.id]?.tab === 'click') ? 'rotate(180deg)' : 'rotate(0deg)' }"
                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"/>
                  </svg>
                </div>
              </td>
              <td class="text-right text-sm">{{ unsubCount(c.id) }}</td>
              <td class="text-xs" style="color:var(--gray-400);">{{ fmtDate(c.created_at) }}</td>
              <td>
                <button class="btn btn-ghost btn-sm" @click.stop="openDetail(c)" title="查看詳情">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </button>
              </td>
            </tr>

            <!-- Expand sub-row -->
            <tr v-if="expandedRows[c.id]?.open" style="background:var(--surface-secondary,var(--gray-50));">
              <td colspan="9" class="px-4 py-3">
                <!-- Loading state -->
                <div v-if="expandedRows[c.id].loading" class="text-sm py-2" style="color:var(--gray-400);">載入中…</div>
                <div v-else-if="expandedRows[c.id].error" class="text-sm py-2" style="color:var(--color-danger);">{{ expandedRows[c.id].error }}</div>
                <template v-else-if="expandedRows[c.id].data">
                  <!-- Tabs + download -->
                  <div class="flex items-center gap-2 mb-2">
                    <button class="btn btn-xs"
                      :style="expandedRows[c.id].tab === 'open' ? 'background:var(--color-success);color:#fff;border:none;' : ''"
                      @click.stop="setExpandRow(c.id, { tab: 'open' })">
                      開信者 ({{ (expandedRows[c.id].data.recipients || []).filter(r => r.is_opened).length }})
                    </button>
                    <button class="btn btn-xs"
                      :style="expandedRows[c.id].tab === 'click' ? 'background:var(--brand-500);color:#fff;border:none;' : ''"
                      @click.stop="setExpandRow(c.id, { tab: 'click' })">
                      點擊者 ({{ (expandedRows[c.id].data.recipients || []).filter(r => r.is_clicked).length }})
                    </button>
                    <button class="btn btn-xs"
                      :style="expandedRows[c.id].tab === 'all' ? 'background:var(--gray-600);color:#fff;border:none;' : ''"
                      @click.stop="setExpandRow(c.id, { tab: 'all' })">
                      全部 ({{ (expandedRows[c.id].data.recipients || []).length }})
                    </button>
                    <button class="btn btn-xs"
                      :style="expandedRows[c.id].tab === 'ai' ? 'background:linear-gradient(135deg,#4f46e5,#7c3aed);color:#fff;border:none;' : ''"
                      @click.stop="setExpandRow(c.id, { tab: 'ai' }); loadAiInsights(c)">
                      🤖 AI 洞察
                    </button>
                    <button class="btn btn-secondary btn-xs ml-auto"
                      @click.stop="exportCampaignCsv(c, expandedRows[c.id].data.recipients || [])">
                      ↓ 下載 CSV
                    </button>
                    <button class="btn btn-ghost btn-xs" style="font-size:1rem;line-height:1;"
                      @click.stop="setExpandRow(c.id, { open: false })">✕</button>
                  </div>

                  <!-- AI Insights Panel -->
                  <template v-if="expandedRows[c.id].tab === 'ai'">
                    <div class="rounded-lg p-4 mt-1 space-y-4" style="background:var(--surface);border:1px solid var(--border);">
                      <!-- Insights -->
                      <div>
                        <div class="flex items-center justify-between mb-2">
                          <div class="text-sm font-bold" style="color:#4f46e5;">📊 行為分析摘要</div>
                          <button class="btn btn-xs btn-outline" :disabled="aiInsights[c.id]?.insightsLoading" @click.stop="fetchInsights(c)">
                            {{ aiInsights[c.id]?.insightsLoading ? '分析中...' : '重新分析' }}
                          </button>
                        </div>
                        <div v-if="aiInsights[c.id]?.insightsLoading" class="text-sm py-3 text-center" style="color:var(--gray-400);">
                          <span class="animate-spin mr-1">⟳</span> AI 分析中，請稍候...
                        </div>
                        <div v-else-if="aiInsights[c.id]?.insightsError" class="text-sm py-2" style="color:var(--color-danger);">{{ aiInsights[c.id].insightsError }}</div>
                        <div v-else-if="aiInsights[c.id]?.insights" class="text-sm whitespace-pre-line" style="color:var(--gray-700);line-height:1.7;">{{ aiInsights[c.id].insights }}</div>
                        <div v-else class="text-sm py-2" style="color:var(--gray-400);">點擊「重新分析」開始 AI 分析</div>
                      </div>
                      <!-- Next Steps -->
                      <div style="border-top:1px solid var(--border);padding-top:1rem;">
                        <div class="flex items-center justify-between mb-2">
                          <div class="text-sm font-bold" style="color:#7c3aed;">🚀 下一步行動建議</div>
                          <button class="btn btn-xs btn-outline" :disabled="aiInsights[c.id]?.stepsLoading" @click.stop="fetchNextSteps(c)">
                            {{ aiInsights[c.id]?.stepsLoading ? '生成中...' : '重新生成' }}
                          </button>
                        </div>
                        <div v-if="aiInsights[c.id]?.stepsLoading" class="text-sm py-3 text-center" style="color:var(--gray-400);">
                          <span class="animate-spin mr-1">⟳</span> AI 生成建議中...
                        </div>
                        <div v-else-if="aiInsights[c.id]?.stepsError" class="text-sm py-2" style="color:var(--color-danger);">{{ aiInsights[c.id].stepsError }}</div>
                        <div v-else-if="aiInsights[c.id]?.steps" class="text-sm whitespace-pre-line" style="color:var(--gray-700);line-height:1.7;">{{ aiInsights[c.id].steps }}</div>
                        <div v-else class="text-sm py-2" style="color:var(--gray-400);">點擊「重新生成」取得建議</div>
                      </div>
                    </div>
                  </template>

                  <!-- Recipient mini-table (hidden in AI tab) -->
                  <div v-else class="overflow-x-auto" style="max-height:220px;overflow-y:auto;border-radius:6px;border:1px solid var(--border);">
                    <table class="data-table" style="font-size:0.78rem;">
                      <thead>
                        <tr>
                          <th class="px-3 py-1.5">Email</th>
                          <th class="px-3 py-1.5" style="width:60px;text-align:center;">開信</th>
                          <th class="px-3 py-1.5" style="width:60px;text-align:center;">點擊</th>
                          <th class="px-3 py-1.5" style="width:120px;">寄出時間</th>
                        </tr>
                      </thead>
                      <tbody>
                        <template v-for="r in expandedRows[c.id].data.recipients || []" :key="r.email">
                          <tr v-if="expandedRows[c.id].tab === 'all' || (expandedRows[c.id].tab === 'open' && r.is_opened) || (expandedRows[c.id].tab === 'click' && r.is_clicked)">
                            <td class="px-3 py-1">{{ r.email }}</td>
                            <td class="px-3 py-1 text-center">
                              <span v-if="r.is_opened" style="color:var(--color-success);">✓</span>
                              <span v-else style="color:var(--gray-300);">—</span>
                            </td>
                            <td class="px-3 py-1 text-center">
                              <span v-if="r.is_clicked" style="color:var(--brand-500);">✓</span>
                              <span v-else style="color:var(--gray-300);">—</span>
                            </td>
                            <td class="px-3 py-1" style="color:var(--gray-500);">{{ r.sent_at ? fmtDateTime(r.sent_at) : '—' }}</td>
                          </tr>
                        </template>
                        <tr v-if="(expandedRows[c.id].data.recipients || []).filter(r => expandedRows[c.id].tab === 'all' || (expandedRows[c.id].tab === 'open' && r.is_opened) || (expandedRows[c.id].tab === 'click' && r.is_clicked)).length === 0">
                          <td colspan="4" class="px-3 py-3 text-center" style="color:var(--gray-400);">無記錄</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </template>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <div v-if="campaigns.length >= pageLimit" class="p-3 text-center">
        <button class="btn btn-ghost btn-sm" @click="loadMore" :disabled="loadingMore">
          {{ loadingMore ? '載入中…' : '載入更多' }}
        </button>
      </div>
    </div>

    </template><!-- /overview -->

    <!-- Scheduled Tab -->
    <template v-if="activeTab === 'scheduled'">
      <div v-if="scheduledLoading" class="card p-10 text-center" style="color:var(--gray-400);">載入中…</div>
      <div v-else-if="!scheduledCampaigns.length" class="card p-10 text-center" style="color:var(--gray-400);">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="margin:0 auto 10px;display:block;opacity:.4;"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        目前沒有排程中的活動
      </div>
      <template v-else>
        <div class="card" style="overflow:hidden;">
          <table class="data-table w-full">
            <thead>
              <tr>
                <th>主旨</th>
                <th>收件人數</th>
                <th>排程時間</th>
                <th>倒計時</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in scheduledCampaigns" :key="c.id">
                <td>
                  <div class="font-medium" style="color:var(--gray-900);">{{ c.subject }}</div>
                  <div class="text-xs" style="color:var(--gray-400);">#{{ c.id }}</div>
                </td>
                <td>{{ c.total_recipients }} 人</td>
                <td>{{ fmtDateTime(c.scheduled_at) }}</td>
                <td>
                  <span class="badge badge-warning">{{ countdown(c.scheduled_at) }}</span>
                </td>
                <td>
                  <button class="btn btn-xs" style="color:var(--color-danger);border-color:var(--color-danger);" @click="cancelScheduled(c)">取消</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </template><!-- /scheduled -->

    <!-- Detail Modal (shared across tabs) -->
    <div v-if="showDetail" class="modal-overlay" @click.self="showDetail = false">
      <div class="modal-box" style="max-width:860px;max-height:90vh;overflow-y:auto;">
        <div class="flex items-start justify-between mb-4">
          <div>
            <h2 class="text-lg font-semibold">{{ selectedCampaign?.subject }}</h2>
            <div class="text-sm mt-1" style="color:var(--gray-500);">活動 #{{ selectedCampaign?.id }} · {{ fmtDate(selectedCampaign?.created_at) }}</div>
          </div>
          <button class="btn btn-ghost btn-sm" @click="showDetail = false">✕</button>
        </div>

        <div v-if="detailLoading" class="py-12 text-center" style="color:var(--gray-400);">載入中…</div>
        <div v-else-if="detailError" class="py-6 text-center" style="color:var(--color-danger);">{{ detailError }}</div>
        <template v-else-if="detail">
          <!-- Detail Summary -->
          <div class="grid gap-3 mb-5" style="grid-template-columns: repeat(4, 1fr);">
            <div class="rounded-lg p-3 text-center" style="background:var(--gray-50);border:1px solid var(--border);">
              <div class="text-xl font-bold" style="color:var(--brand-600);">{{ (detail.campaign?.total_recipients || 0).toLocaleString() }}</div>
              <div class="text-xs mt-0.5" style="color:var(--gray-500);">總寄出</div>
            </div>
            <div class="rounded-lg p-3 text-center" style="background:var(--gray-50);border:1px solid var(--border);">
              <div class="text-xl font-bold" :style="{ color: rateColor((detail.stats?.open_rate || 0) / 100) }">{{ (detail.stats?.open_rate ?? 0).toFixed(1) }}%</div>
              <div class="text-xs mt-0.5" style="color:var(--gray-500);">開信率（{{ detail.stats?.unique_opens || 0 }}）</div>
            </div>
            <div class="rounded-lg p-3 text-center" style="background:var(--gray-50);border:1px solid var(--border);">
              <div class="text-xl font-bold" :style="{ color: rateColor((detail.stats?.click_rate || 0) / 100) }">{{ (detail.stats?.click_rate ?? 0).toFixed(1) }}%</div>
              <div class="text-xs mt-0.5" style="color:var(--gray-500);">點擊率（{{ detail.stats?.unique_clicks || 0 }}）</div>
            </div>
            <div class="rounded-lg p-3 text-center" style="background:var(--gray-50);border:1px solid var(--border);">
              <div class="text-xl font-bold" style="color:var(--color-warning);">{{ unsubCount(selectedCampaign?.id) }}</div>
              <div class="text-xs mt-0.5" style="color:var(--gray-500);">退訂數</div>
            </div>
          </div>

          <!-- Event Timeline Chart -->
          <div v-if="detail.chart_data?.length" class="mb-5">
            <div class="font-medium text-sm mb-2">事件時間軸</div>
            <div class="rounded-lg p-3" style="background:var(--gray-50);border:1px solid var(--border);">
              <div class="flex gap-1 items-end" style="height:80px;">
                <div
                  v-for="(bar, i) in timelineChart"
                  :key="i"
                  class="flex-1 rounded-sm"
                  :style="{ height: (bar.height * 80) + 'px', background: bar.hasClick ? 'var(--brand-400)' : 'var(--color-success)', opacity: 0.85 }"
                  :title="bar.label"
                ></div>
              </div>
              <div class="flex justify-between text-xs mt-1" style="color:var(--gray-400);">
                <span>{{ detail.chart_data[detail.chart_data.length - 1]?.timestamp?.slice(0, 10) }}</span>
                <span class="flex gap-3">
                  <span><span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:var(--color-success);margin-right:3px;"></span>開信</span>
                  <span><span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:var(--brand-400);margin-right:3px;"></span>點擊</span>
                </span>
                <span>{{ detail.chart_data[0]?.timestamp?.slice(0, 10) }}</span>
              </div>
            </div>
          </div>

          <!-- Activity Log -->
          <div v-if="detail.logs?.length" class="mb-5">
            <div class="font-medium text-sm mb-2">最近活動（最多 50 筆）</div>
            <div class="rounded-lg overflow-hidden" style="border:1px solid var(--border);">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>收件人</th>
                    <th>事件</th>
                    <th>時間</th>
                    <th>連結</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(log, i) in detail.logs" :key="i">
                    <td class="text-sm">{{ log.recipient }}</td>
                    <td>
                      <span class="badge" :class="log.event === 'open' ? 'badge-success' : 'badge-info'">
                        {{ log.event === 'open' ? '開信' : '點擊' }}
                      </span>
                    </td>
                    <td class="text-xs" style="color:var(--gray-500);">{{ fmtDateTime(log.timestamp) }}</td>
                    <td class="text-xs truncate" style="max-width:200px;color:var(--gray-400);">{{ log.details || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Recipient Status -->
          <div v-if="detail.recipients?.length">
            <div class="flex items-center gap-2 mb-2">
              <div class="font-medium text-sm">收件人狀態</div>
              <button class="btn btn-secondary btn-xs ml-auto"
                @click="exportCampaignCsv(selectedCampaign, detail.recipients)">
                ↓ 下載 CSV
              </button>
            </div>
            <div class="rounded-lg overflow-hidden" style="border:1px solid var(--border);">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>狀態</th>
                    <th style="text-align:center;">開信</th>
                    <th style="text-align:center;">點擊</th>
                    <th>寄出時間</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="r in detail.recipients" :key="r.email">
                    <td class="text-sm">{{ r.email }}</td>
                    <td>
                      <span class="badge" :class="recipientStatusBadge(r.status)">{{ recipientStatusLabel(r.status) }}</span>
                    </td>
                    <td class="text-center">
                      <span v-if="r.is_opened" style="color:var(--color-success);">✓</span>
                      <span v-else style="color:var(--gray-300);">—</span>
                    </td>
                    <td class="text-center">
                      <span v-if="r.is_clicked" style="color:var(--brand-500);">✓</span>
                      <span v-else style="color:var(--gray-300);">—</span>
                    </td>
                    <td class="text-xs" style="color:var(--gray-500);">{{ r.sent_at ? fmtDateTime(r.sent_at) : '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Journey Stats Tab -->
    <template v-if="activeTab === 'journey'">
      <!-- Journey Summary Cards -->
      <div class="grid grid-cols-2 gap-4" style="grid-template-columns:repeat(4,1fr);">
        <div class="card p-5 text-center">
          <div class="text-2xl font-bold" style="color:var(--brand-600);">{{ journeys.length }}</div>
          <div class="text-sm mt-1" style="color:var(--gray-500);">旅程總數</div>
        </div>
        <div class="card p-5 text-center">
          <div class="text-2xl font-bold" style="color:var(--color-success);">{{ journeys.filter(j => j.status === 'active').length }}</div>
          <div class="text-sm mt-1" style="color:var(--gray-500);">進行中</div>
        </div>
        <div class="card p-5 text-center">
          <div class="text-2xl font-bold" style="color:var(--gray-700);">{{ journeys.reduce((s, j) => s + (j._stats?.enrollments_total || 0), 0).toLocaleString() }}</div>
          <div class="text-sm mt-1" style="color:var(--gray-500);">總訂閱人數</div>
        </div>
        <div class="card p-5 text-center">
          <div class="text-2xl font-bold" :style="{ color: rateColor(avgJourneyOpenRate) }">{{ pct(avgJourneyOpenRate) }}</div>
          <div class="text-sm mt-1" style="color:var(--gray-500);">平均開信率</div>
        </div>
      </div>

      <!-- Journey Table -->
      <div v-if="journeyLoading" class="card p-10 text-center" style="color:var(--gray-400);">
        <span class="animate-spin mr-2">⟳</span> 載入中…
      </div>
      <div v-else-if="!journeys.length" class="card p-10 text-center" style="color:var(--gray-400);">尚無旅程資料</div>
      <div v-else class="card overflow-hidden">
        <table class="data-table">
          <thead>
            <tr>
              <th>旅程名稱</th>
              <th style="width:80px;text-align:center;">狀態</th>
              <th style="width:100px;text-align:right;">訂閱人數</th>
              <th style="width:100px;text-align:right;">進行中</th>
              <th style="width:100px;text-align:right;">已完成</th>
              <th style="width:180px;">完成率</th>
              <th style="width:180px;">步驟1 開信率</th>
              <th style="width:120px;">建立時間</th>
              <th style="width:100px;"></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="j in journeys" :key="j.id">
            <tr>
              <td>
                <div class="font-medium">{{ j.name || '（未命名）' }}</div>
                <div class="text-xs mt-0.5" style="color:var(--gray-400);">{{ (j.steps || []).length }} 個步驟</div>
              </td>
              <td class="text-center">
                <span class="badge" :class="j.status === 'active' ? 'badge-success' : (j.status === 'paused' ? 'badge-warning' : '')">
                  {{ journeyStatusLabel(j.status) }}
                </span>
              </td>
              <td class="text-right font-mono">{{ j._stats?.enrollments_total ?? '—' }}</td>
              <td class="text-right font-mono">{{ j._stats?.enrollments_active ?? '—' }}</td>
              <td class="text-right font-mono">{{ j._stats?.enrollments_completed ?? '—' }}</td>
              <td>
                <div class="flex items-center gap-2">
                  <div class="flex-1 rounded-full overflow-hidden" style="height:6px;background:var(--gray-100);">
                    <div class="h-full rounded-full" :style="{ width: Math.min(100, (j._stats?.completed_rate || 0) * 100) + '%', background: 'var(--color-success)' }"></div>
                  </div>
                  <span class="text-xs w-12 text-right" :style="{ color: rateColor(j._stats?.completed_rate || 0) }">
                    {{ j._stats ? pct(j._stats.completed_rate || 0) : '—' }}
                  </span>
                </div>
              </td>
              <td>
                <div class="flex items-center gap-2">
                  <div class="flex-1 rounded-full overflow-hidden" style="height:6px;background:var(--gray-100);">
                    <div class="h-full rounded-full" :style="{ width: Math.min(100, (j._stats?.open_rate_step1 || 0) * 100) + '%', background: 'var(--brand-500)' }"></div>
                  </div>
                  <span class="text-xs w-12 text-right" :style="{ color: rateColor(j._stats?.open_rate_step1 || 0) }">
                    {{ j._stats ? pct(j._stats.open_rate_step1 || 0) : '—' }}
                  </span>
                </div>
              </td>
              <td class="text-xs" style="color:var(--gray-400);">{{ fmtDate(j.created_at) }}</td>
              <td class="text-center">
                <button class="btn btn-xs" style="background:#4f46e5;color:#fff;border:none;white-space:nowrap;"
                    :disabled="journeyOptimize[j.id]?.loading"
                    @click="fetchJourneyOptimize(j)">
                  {{ journeyOptimize[j.id]?.loading ? '分析中...' : '🤖 AI 優化' }}
                </button>
              </td>
            </tr>
            <!-- AI optimize expand row -->
            <tr v-if="journeyOptimize[j.id]?.result || journeyOptimize[j.id]?.error">
              <td colspan="9" style="background:#f5f3ff;padding:1rem 1.5rem;">
                <div v-if="journeyOptimize[j.id]?.error" class="text-sm" style="color:var(--color-danger);">{{ journeyOptimize[j.id].error }}</div>
                <div v-else class="text-sm whitespace-pre-line" style="color:#3730a3;line-height:1.7;">{{ journeyOptimize[j.id].result }}</div>
              </td>
            </tr>
            </template>
          </tbody>
        </table>
      </div>
    </template>

    <!-- 名單健康度 Tab -->
    <template v-if="activeTab === 'list_health'">
      <div class="card p-5 space-y-4">
        <div class="flex items-center justify-between">
          <div>
            <div class="font-bold text-base">📊 名單活躍度評分</div>
            <div class="text-sm mt-0.5" style="color:var(--gray-500);">依寄送歷史（開信率、點擊率、近期互動）為每個聯絡人計算活躍分數（0-100）</div>
          </div>
          <div class="flex flex-wrap gap-2">
            <button class="btn btn-sm btn-outline" :disabled="listHealthLoading" @click="runContactScore">
              {{ listHealthLoading ? '評分中...' : '重新評分' }}
            </button>
            <button class="btn btn-sm btn-outline" :disabled="bestSendLoading" @click="runComputeBestSendTime">
              {{ bestSendLoading ? '計算中...' : '📅 計算最佳寄送時段' }}
            </button>
            <button class="btn btn-sm" style="background:#4f46e5;color:#fff;border:none;" :disabled="listHealthAiLoading || !listHealthScored" @click="runContactAiHealth">
              {{ listHealthAiLoading ? 'AI 分析中...' : '🤖 AI 健康診斷' }}
            </button>
          </div>
        </div>

        <!-- Summary chips -->
        <div v-if="listHealthSummary" class="grid grid-cols-3 gap-3">
          <div class="rounded-lg p-3 text-center" style="background:#dcfce7;border:1px solid #86efac;">
            <div class="text-xl font-bold" style="color:#15803d;">{{ listHealthSummary.healthy }}</div>
            <div class="text-xs mt-0.5" style="color:#15803d;">健康（≥50）</div>
          </div>
          <div class="rounded-lg p-3 text-center" style="background:#fef3c7;border:1px solid #fcd34d;">
            <div class="text-xl font-bold" style="color:#b45309;">{{ listHealthSummary.at_risk }}</div>
            <div class="text-xs mt-0.5" style="color:#b45309;">待觀察（20-49）</div>
          </div>
          <div class="rounded-lg p-3 text-center" style="background:#fee2e2;border:1px solid #fca5a5;">
            <div class="text-xl font-bold" style="color:#b91c1c;">{{ listHealthSummary.inactive }}</div>
            <div class="text-xs mt-0.5" style="color:#b91c1c;">沈睡（&lt;20）</div>
          </div>
        </div>

        <!-- AI Health Report -->
        <div v-if="listHealthAiResult || listHealthAiLoading" class="rounded-lg p-4" style="background:linear-gradient(135deg,#eef2ff,#f5f3ff);border:1px solid #c7d2fe;">
          <div class="text-sm font-bold mb-2" style="color:#4f46e5;">🤖 AI 名單健康診斷</div>
          <div v-if="listHealthAiLoading" class="text-sm py-2 text-center" style="color:var(--gray-400);"><span class="animate-spin mr-1">⟳</span> 分析中...</div>
          <div v-else class="text-sm whitespace-pre-line" style="color:var(--gray-700);line-height:1.7;">{{ listHealthAiResult }}</div>
        </div>

        <!-- Contacts Table -->
        <div v-if="listHealthContacts.length" class="overflow-x-auto" style="max-height:400px;overflow-y:auto;border-radius:8px;border:1px solid var(--border);">
          <table class="data-table" style="font-size:0.82rem;">
            <thead>
              <tr>
                <th class="px-3 py-2">Email</th>
                <th class="px-3 py-2">姓名</th>
                <th class="px-3 py-2" style="width:80px;text-align:center;">活躍分數</th>
                <th class="px-3 py-2" style="width:80px;text-align:center;">風險</th>
                <th class="px-3 py-2" style="width:90px;text-align:center;">最佳寄送時段</th>
                <th class="px-3 py-2" style="width:70px;text-align:right;">已寄出</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in listHealthContacts" :key="c.email">
                <td class="px-3 py-1.5">{{ c.email }}</td>
                <td class="px-3 py-1.5" style="color:var(--gray-600);">{{ c.name || '—' }}</td>
                <td class="px-3 py-1.5 text-center">
                  <span class="font-bold" :style="{ color: c.activity_score >= 50 ? 'var(--color-success)' : (c.activity_score >= 20 ? 'var(--color-warning)' : 'var(--color-danger)') }">
                    {{ c.activity_score }}
                  </span>
                </td>
                <td class="px-3 py-1.5 text-center">
                  <span class="badge" :class="c.risk === 'healthy' ? 'badge-success' : (c.risk === 'at_risk' ? 'badge-warning' : 'badge-danger')">
                    {{ c.risk === 'healthy' ? '健康' : (c.risk === 'at_risk' ? '待觀察' : '沈睡') }}
                  </span>
                </td>
                <td class="px-3 py-1.5 text-center text-xs" style="color:var(--gray-600);">
                  <span v-if="bestSendResult?.contacts?.find(b => b.email === c.email)" style="color:#7c3aed;font-weight:600;">
                    {{ bestSendResult.contacts.find(b => b.email === c.email).best_hour }}:00
                  </span>
                  <span v-else style="color:var(--gray-400);">—</span>
                </td>
                <td class="px-3 py-1.5 text-right">{{ c.total_sent }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else-if="!listHealthLoading" class="text-sm py-4 text-center" style="color:var(--gray-400);">點擊「重新評分」開始評估名單健康度</div>

        <!-- Auto Tag -->
        <div style="border-top:1px solid var(--border);padding-top:1.25rem;">
          <div class="flex items-center justify-between mb-3">
            <div>
              <div class="font-bold text-sm">🏷️ 興趣分群自動標籤</div>
              <div class="text-xs mt-0.5" style="color:var(--gray-500);">依每個聯絡人的點擊連結，AI 自動分析並補上興趣標籤到聯絡人資料</div>
            </div>
            <button class="btn btn-sm btn-outline" :disabled="autoTagLoading" @click="runAutoTag">
              {{ autoTagLoading ? 'AI 標記中...' : '🤖 執行自動標籤' }}
            </button>
          </div>
          <div v-if="autoTagResult" class="rounded-lg p-3 text-sm" style="background:var(--gray-50);border:1px solid var(--border);">
            <div class="font-medium mb-1">已更新 <strong>{{ autoTagResult.updated }}</strong> 位聯絡人的標籤</div>
            <div v-if="autoTagResult.preview?.length" class="space-y-1 mt-2" style="max-height:150px;overflow-y:auto;">
              <div v-for="p in autoTagResult.preview" :key="p.email" class="flex items-center gap-2 text-xs">
                <span style="color:var(--gray-600);">{{ p.email }}</span>
                <span v-for="t in p.new_tags" :key="t" class="badge badge-info" style="font-size:0.7rem;">{{ t }}</span>
              </div>
            </div>
          </div>
          <div v-if="autoTagError" class="text-xs mt-2" style="color:var(--color-danger);">{{ autoTagError }}</div>
        </div>

        <!-- Feature 2: 再激活活動 -->
        <div style="border-top:1px solid var(--border);padding-top:1.25rem;">
          <div class="flex items-center justify-between mb-3">
            <div>
              <div class="font-bold text-sm">🔄 再激活活動自動產生</div>
              <div class="text-xs mt-0.5" style="color:var(--gray-500);">
                對活躍分數 &lt;20 的沈睡聯絡人，AI 自動生成一封喚醒信，一鍵寄出
              </div>
            </div>
            <button class="btn btn-sm" style="background:linear-gradient(135deg,#059669,#0d9488);color:#fff;border:none;" :disabled="reactivationLoading" @click="runGenerateReactivation">
              {{ reactivationLoading ? 'AI 生成中...' : '🤖 AI 生成喚醒郵件' }}
            </button>
          </div>

          <div v-if="reactivationResult" class="space-y-3">
            <div class="rounded-lg p-4" style="background:linear-gradient(135deg,#ecfdf5,#f0fdf4);border:1px solid #86efac;">
              <div class="text-xs font-bold mb-1" style="color:#15803d;">📋 生成結果（{{ reactivationResult.inactive_count }} 位沈睡聯絡人）</div>
              <div class="text-sm font-semibold mb-1">主旨：{{ reactivationResult.subject }}</div>
              <div class="text-xs" style="color:var(--gray-500);">（HTML 內文已生成，點擊「確認發送」即寄出）</div>
            </div>
            <div class="flex items-center gap-3">
              <button class="btn btn-sm"
                style="background:#b91c1c;color:#fff;border:none;"
                :disabled="reactivationSending || !reactivationResult.emails?.length"
                @click="runSendReactivation">
                {{ reactivationSending ? '寄送中...' : `📤 確認發送給 ${reactivationResult.emails?.length || 0} 位沈睡聯絡人` }}
              </button>
              <button class="btn btn-sm btn-outline" @click="reactivationResult = null">取消</button>
            </div>
            <div v-if="reactivationSentId" class="text-xs" style="color:var(--color-success);">
              ✓ 已啟動再激活活動，campaign_id={{ reactivationSentId }}
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- 退訂分析 Tab -->
    <template v-if="activeTab === 'unsub_analysis'">
      <div class="card p-5 space-y-4">
        <div class="flex items-center justify-between">
          <div>
            <div class="font-bold text-base">📤 退訂原因分析</div>
            <div class="text-sm mt-0.5" style="color:var(--gray-500);">收集退訂者填寫的原因，由 AI 歸納改善建議</div>
          </div>
          <button class="btn btn-sm" style="background:#4f46e5;color:#fff;border:none;" :disabled="unsubAiLoading" @click="runUnsubAiClassify">
            {{ unsubAiLoading ? 'AI 分析中...' : '🤖 AI 退訂分析' }}
          </button>
        </div>

        <!-- Reason breakdown chart (bar) -->
        <div v-if="unsubscribes.length">
          <div class="text-sm font-semibold mb-2" style="color:var(--gray-700);">退訂原因分布（{{ confirmedUnsubscribes.length }} 筆已確認）</div>
          <div class="space-y-2">
            <div v-for="(count, label) in unsubReasonBreakdown" :key="label" class="flex items-center gap-3">
              <div class="text-xs w-28 text-right flex-shrink-0" style="color:var(--gray-600);">{{ label }}</div>
              <div class="flex-1 rounded-full overflow-hidden" style="height:10px;background:var(--gray-100);">
                <div class="h-full rounded-full" style="background:#6366f1;"
                  :style="{ width: (count / Math.max(...Object.values(unsubReasonBreakdown)) * 100) + '%' }"></div>
              </div>
              <div class="text-xs w-8 text-right font-mono" style="color:var(--gray-600);">{{ count }}</div>
            </div>
            <div class="flex items-center gap-3">
              <div class="text-xs w-28 text-right flex-shrink-0" style="color:var(--gray-400);">未填寫原因</div>
              <div class="flex-1 rounded-full overflow-hidden" style="height:10px;background:var(--gray-100);">
                <div class="h-full rounded-full" style="background:var(--gray-300);"
                  :style="{ width: (unsubNoReasonCount / Math.max(confirmedUnsubscribes.length, 1) * 100) + '%' }"></div>
              </div>
              <div class="text-xs w-8 text-right font-mono" style="color:var(--gray-400);">{{ unsubNoReasonCount }}</div>
            </div>
          </div>
        </div>
        <div v-else class="text-sm py-4 text-center" style="color:var(--gray-400);">尚無退訂記錄</div>

        <!-- AI Report -->
        <div v-if="unsubAiResult || unsubAiLoading" class="rounded-lg p-4" style="background:linear-gradient(135deg,#eef2ff,#f5f3ff);border:1px solid #c7d2fe;">
          <div class="text-sm font-bold mb-2" style="color:#4f46e5;">🤖 AI 退訂分析報告</div>
          <div v-if="unsubAiLoading" class="text-sm py-2 text-center" style="color:var(--gray-400);"><span class="animate-spin mr-1">⟳</span> 分析中...</div>
          <div v-else class="text-sm whitespace-pre-line" style="color:var(--gray-700);line-height:1.7;">{{ unsubAiResult }}</div>
        </div>
      </div>
    </template>

    <!-- 回覆分類 Tab -->
    <template v-if="activeTab === 'replies'">
      <div class="card p-5 space-y-4">
        <div class="flex items-center justify-between">
          <div>
            <div class="font-bold text-base">📬 回覆郵件 AI 分類</div>
            <div class="text-sm mt-0.5" style="color:var(--gray-500);">掃描收件匣中來自行銷收件人的回覆，AI 自動分類意圖並標記需處理項目</div>
          </div>
          <button class="btn btn-sm" style="background:#4f46e5;color:#fff;border:none;" :disabled="repliesLoading" @click="runScanReplies">
            {{ repliesLoading ? '掃描中...' : '🔍 掃描回覆' }}
          </button>
        </div>

        <div v-if="repliesError" class="text-sm rounded p-3" style="background:#fee2e2;color:#b91c1c;">{{ repliesError }}</div>

        <div v-if="repliesResult" class="space-y-2">
          <div class="text-xs" style="color:var(--gray-500);">
            掃描 {{ repliesResult.scanned }} 封郵件，命中 {{ repliesResult.matched }} 封行銷回覆
          </div>
          <div v-if="!repliesResult.classified?.length" class="text-sm py-4 text-center" style="color:var(--gray-400);">未找到來自行銷收件人的回覆</div>
          <div v-else class="overflow-x-auto rounded-lg" style="border:1px solid var(--border);">
            <table class="data-table" style="font-size:0.82rem;">
              <thead>
                <tr>
                  <th class="px-3 py-2">寄件人</th>
                  <th class="px-3 py-2">主旨</th>
                  <th class="px-3 py-2" style="width:110px;text-align:center;">意圖分類</th>
                  <th class="px-3 py-2" style="width:70px;text-align:center;">優先度</th>
                  <th class="px-3 py-2">摘要</th>
                  <th class="px-3 py-2">建議行動</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, idx) in repliesResult.classified" :key="idx" :style="r.priority === 'high' ? 'background:#fff7ed;' : ''">
                  <td class="px-3 py-2 text-xs">{{ r.from_email }}</td>
                  <td class="px-3 py-2 text-xs" style="max-width:180px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">{{ r.subject || '—' }}</td>
                  <td class="px-3 py-2 text-center">
                    <span class="badge"
                      :class="r.intent === '詢問' ? 'badge-info' : r.intent === '正面回應' ? 'badge-success' : r.intent === '退訂請求' ? 'badge-warning' : r.intent === '負面反饋' ? 'badge-danger' : ''">
                      {{ r.intent || '—' }}
                    </span>
                  </td>
                  <td class="px-3 py-2 text-center">
                    <span class="badge" :class="r.priority === 'high' ? 'badge-danger' : r.priority === 'medium' ? 'badge-warning' : 'badge-default'">
                      {{ r.priority === 'high' ? '緊急' : r.priority === 'medium' ? '一般' : '低' }}
                    </span>
                  </td>
                  <td class="px-3 py-2 text-xs">{{ r.summary || '—' }}</td>
                  <td class="px-3 py-2 text-xs" style="color:var(--brand-600);">{{ r.action || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-else-if="!repliesLoading" class="text-sm py-4 text-center" style="color:var(--gray-400);">
          點擊「掃描回覆」開始掃描（需要已登入信箱工作階段）
        </div>
      </div>
    </template>

    <!-- ── 競爭者 EDM 分析 ── -->
    <template v-if="activeTab === 'competitor'">
      <div class="card p-5 space-y-4">
        <div class="font-bold text-base">🔬 競爭者 EDM 分析</div>
        <div class="text-sm" style="color:var(--gray-500);">貼入競爭者寄來的郵件主旨與內容，AI 拆解其行銷策略與差異化機會</div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="space-y-3">
            <div>
              <label class="block text-xs font-medium mb-1">競爭者郵件主旨</label>
              <input v-model="competitorSubject" class="input w-full" placeholder="例如：限時 48 小時！超殺折扣最後倒數" />
            </div>
            <div>
              <label class="block text-xs font-medium mb-1">郵件 HTML 內容（可選）</label>
              <textarea v-model="competitorHtml" class="input w-full" rows="4" placeholder="貼入 HTML 原始碼或純文字內容" style="font-family:monospace;font-size:0.78rem;resize:vertical;"></textarea>
            </div>
            <div>
              <label class="block text-xs font-medium mb-1">純文字版本（可選）</label>
              <textarea v-model="competitorText" class="input w-full" rows="3" placeholder="郵件純文字版本" style="font-size:0.78rem;resize:vertical;"></textarea>
            </div>
            <button @click="runCompetitorAnalysis" :disabled="competitorLoading"
              class="btn w-full" style="background:#4f46e5;color:#fff;border:none;">
              {{ competitorLoading ? '分析中...' : '🔬 開始分析' }}
            </button>
            <div v-if="competitorError" class="text-xs rounded p-2" style="background:#fee2e2;color:#b91c1c;">{{ competitorError }}</div>
          </div>
          <div v-if="competitorResult" class="space-y-3 overflow-y-auto" style="max-height:500px;">
            <div v-for="section in competitorResult.sections" :key="section.title"
              class="rounded-lg p-3" style="background:#f5f3ff;border:1px solid #e0e7ff;">
              <div class="font-bold text-sm mb-1" style="color:#4f46e5;">{{ section.title }}</div>
              <div class="text-xs whitespace-pre-line" style="color:var(--gray-700);">{{ section.content }}</div>
            </div>
          </div>
          <div v-else-if="!competitorLoading" class="flex items-center justify-center" style="color:var(--gray-300);font-size:2rem;">🔬</div>
        </div>
      </div>
    </template>

    <!-- ── 月度績效報告 ── -->
    <template v-if="activeTab === 'monthly_report'">
      <div class="card p-5 space-y-4">
        <div class="flex items-center justify-between">
          <div>
            <div class="font-bold text-base">📈 月度績效自動報告</div>
            <div class="text-sm mt-0.5" style="color:var(--gray-500);">AI 自動彙整當月行銷數據，生成完整敘述性報告</div>
          </div>
          <div class="flex items-center gap-2">
            <select v-model="reportYear" class="input" style="height:32px;font-size:0.8rem;">
              <option v-for="y in reportYearOptions" :key="y" :value="y">{{ y }} 年</option>
            </select>
            <select v-model="reportMonth" class="input" style="height:32px;font-size:0.8rem;">
              <option v-for="m in 12" :key="m" :value="m">{{ m }} 月</option>
            </select>
            <button @click="runMonthlyReport" :disabled="monthlyReportLoading"
              class="btn btn-sm" style="background:#4f46e5;color:#fff;border:none;white-space:nowrap;">
              {{ monthlyReportLoading ? '生成中...' : '📈 生成報告' }}
            </button>
          </div>
        </div>
        <div v-if="monthlyReportError" class="text-sm rounded p-3" style="background:#fee2e2;color:#b91c1c;">{{ monthlyReportError }}</div>
        <div v-if="monthlyReportResult" class="space-y-4">
          <!-- 數據摘要卡 -->
          <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div class="rounded-lg p-3 text-center" style="background:#eff6ff;border:1px solid #bfdbfe;">
              <div class="text-xs" style="color:var(--gray-500);">活動數</div>
              <div class="text-2xl font-bold" style="color:#2563eb;">{{ monthlyReportResult.total_campaigns }}</div>
            </div>
            <div class="rounded-lg p-3 text-center" style="background:#f0fdf4;border:1px solid #bbf7d0;">
              <div class="text-xs" style="color:var(--gray-500);">總寄送</div>
              <div class="text-2xl font-bold" style="color:#16a34a;">{{ monthlyReportResult.total_sent }}</div>
            </div>
            <div class="rounded-lg p-3 text-center" style="background:#fff7ed;border:1px solid #fed7aa;">
              <div class="text-xs" style="color:var(--gray-500);">平均開信率</div>
              <div class="text-2xl font-bold" style="color:#ea580c;">{{ (monthlyReportResult.avg_open_rate * 100).toFixed(1) }}%</div>
            </div>
            <div class="rounded-lg p-3 text-center" style="background:#f5f3ff;border:1px solid #ddd6fe;">
              <div class="text-xs" style="color:var(--gray-500);">平均點擊率</div>
              <div class="text-2xl font-bold" style="color:#7c3aed;">{{ (monthlyReportResult.avg_click_rate * 100).toFixed(1) }}%</div>
            </div>
          </div>
          <!-- AI 敘述報告 -->
          <div v-if="monthlyReportResult.ai_narrative" class="rounded-lg p-4 space-y-3" style="background:#fafafa;border:1px solid var(--border);">
            <div class="font-bold text-sm" style="color:#4f46e5;">🤖 AI 月度分析報告</div>
            <div class="text-sm whitespace-pre-line" style="color:var(--gray-700);line-height:1.7;">{{ monthlyReportResult.ai_narrative }}</div>
          </div>
        </div>
        <div v-else-if="!monthlyReportLoading" class="text-sm py-6 text-center" style="color:var(--gray-400);">
          選擇年月後點擊「生成報告」
        </div>
      </div>
    </template>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useAppStore, ADMIN_API } from '../store/app.js'
import { storeToRefs } from 'pinia'
import axios from 'axios'

const store = useAppStore()
const { aiMarketingState, marketingReportPdfLoading } = storeToRefs(store)
const { exportMarketingReportPdf } = store

const activeTab = ref('overview')

const campaigns = ref([])
const loading = ref(false)
const loadingMore = ref(false)
const pageLimit = 50
const offset = ref(0)

const unsubscribes = ref([])

const filterQ = ref('')
const filterStatus = ref('')

// Feature 7: 旅程節點 AI 優化建議
const journeyOptimize = ref({})
const setJourneyOptimize = (id, patch) => {
  journeyOptimize.value = { ...journeyOptimize.value, [id]: { ...(journeyOptimize.value[id] || {}), ...patch } }
}
const fetchJourneyOptimize = async (j) => {
  if (journeyOptimize.value[j.id]?.loading) return
  setJourneyOptimize(j.id, { loading: true, result: null, error: null })
  try {
    const res = await axios.get(`${ADMIN_API}/ai/marketing/journey/${j.id}/ai_optimize`)
    setJourneyOptimize(j.id, { result: res.data?.result || '（無內容）', loading: false })
  } catch (e) {
    setJourneyOptimize(j.id, { error: e.response?.data?.detail || e.message, loading: false })
  }
}

// Feature 8: 回覆郵件 AI 分類
const repliesLoading = ref(false)
const repliesResult = ref(null)
const repliesError = ref('')
const runScanReplies = async () => {
  const mailSession = store.mailSessionToken || ''
  if (!mailSession) {
    window.$toast?.error('需要郵箱工作階段', '請先在 EDM 生成頁登入信箱再執行掃描')
    return
  }
  repliesLoading.value = true
  repliesResult.value = null
  repliesError.value = ''
  try {
    const res = await axios.post(`${ADMIN_API}/ai/marketing/replies/scan`, {}, {
      headers: { 'X-Mail-Session': mailSession }
    })
    repliesResult.value = res.data
  } catch (e) {
    repliesError.value = e.response?.data?.detail || e.message
  } finally {
    repliesLoading.value = false
  }
}

// Feature 1: 最佳寄送時段
const bestSendLoading = ref(false)
const bestSendResult = ref(null)

const runComputeBestSendTime = async () => {
  bestSendLoading.value = true
  try {
    const res = await axios.post(`${ADMIN_API}/ai/marketing/contacts/compute_best_send_time`)
    bestSendResult.value = res.data
    window.$toast?.success('計算完成', `已更新 ${res.data.updated} 位聯絡人的最佳寄送時段`)
  } catch (e) {
    window.$toast?.error('計算失敗', e.response?.data?.detail || e.message)
  } finally {
    bestSendLoading.value = false
  }
}

// Feature 2: 再激活活動
const reactivationLoading = ref(false)
const reactivationResult = ref(null)
const reactivationSending = ref(false)
const reactivationSentId = ref(null)

const runGenerateReactivation = async () => {
  reactivationLoading.value = true
  reactivationResult.value = null
  reactivationSentId.value = null
  try {
    const res = await axios.post(`${ADMIN_API}/ai/marketing/reactivation/generate`)
    reactivationResult.value = res.data
    if (!res.data.inactive_count) {
      window.$toast?.info('無沈睡聯絡人', '目前沒有活躍分數 <20 的聯絡人，請先執行評分')
      reactivationResult.value = null
    }
  } catch (e) {
    window.$toast?.error('生成失敗', e.response?.data?.detail || e.message)
  } finally {
    reactivationLoading.value = false
  }
}

const runSendReactivation = async () => {
  if (!reactivationResult.value) return
  const mailSession = store.mailSessionToken || ''
  if (!mailSession) {
    window.$toast?.error('需要郵箱工作階段', '請先在 EDM 生成頁登入信箱再執行寄送')
    return
  }
  if (!confirm(`確定要寄送喚醒信給 ${reactivationResult.value.emails?.length} 位沈睡聯絡人？`)) return
  reactivationSending.value = true
  try {
    const res = await axios.post(`${ADMIN_API}/ai/marketing/reactivation/send`, {
      subject: reactivationResult.value.subject,
      body_html: reactivationResult.value.body_html,
      emails: reactivationResult.value.emails,
    }, { headers: { 'X-Mail-Session': mailSession } })
    reactivationSentId.value = res.data.campaign_id
    window.$toast?.success('已啟動', `再激活活動已開始寄送，campaign_id=${res.data.campaign_id}`)
  } catch (e) {
    window.$toast?.error('寄送失敗', e.response?.data?.detail || e.message)
  } finally {
    reactivationSending.value = false
  }
}

// Feature 9: 名單健康度
const listHealthLoading = ref(false)
const listHealthContacts = ref([])
const listHealthSummary = ref(null)
const listHealthScored = ref(false)
const listHealthAiLoading = ref(false)
const listHealthAiResult = ref('')

const runContactScore = async () => {
  listHealthLoading.value = true
  listHealthContacts.value = []
  listHealthSummary.value = null
  try {
    const res = await axios.post(`${ADMIN_API}/ai/marketing/contacts/score`)
    listHealthSummary.value = res.data.summary
    listHealthContacts.value = res.data.contacts || []
    listHealthScored.value = true
  } catch (e) {
    window.$toast?.error('評分失敗', e.response?.data?.detail || e.message)
  } finally {
    listHealthLoading.value = false
  }
}

const runContactAiHealth = async () => {
  listHealthAiLoading.value = true
  listHealthAiResult.value = ''
  try {
    const res = await axios.get(`${ADMIN_API}/ai/marketing/contacts/ai_health`)
    listHealthAiResult.value = res.data?.result || '（無內容）'
  } catch (e) {
    window.$toast?.error('AI 分析失敗', e.response?.data?.detail || e.message)
  } finally {
    listHealthAiLoading.value = false
  }
}

// Feature 10: 自動標籤
const autoTagLoading = ref(false)
const autoTagResult = ref(null)
const autoTagError = ref('')

const runAutoTag = async () => {
  autoTagLoading.value = true
  autoTagResult.value = null
  autoTagError.value = ''
  try {
    const res = await axios.post(`${ADMIN_API}/ai/marketing/contacts/auto_tag`)
    autoTagResult.value = res.data
  } catch (e) {
    autoTagError.value = e.response?.data?.detail || e.message
  } finally {
    autoTagLoading.value = false
  }
}

// Feature 7: 退訂分析
const unsubAiLoading = ref(false)
const unsubAiResult = ref('')

const reasonLabel = {
  too_frequent: '收到太多郵件',
  not_relevant: '內容與我無關',
  never_subscribed: '我從未訂閱',
  switched_service: '已改用其他服務',
  other: '其他原因',
}

const confirmedUnsubscribes = computed(() => unsubscribes.value)

const unsubReasonBreakdown = computed(() => {
  const counts = {}
  for (const r of unsubscribes.value) {
    if (r.reason) {
      const label = reasonLabel[r.reason] || r.reason
      counts[label] = (counts[label] || 0) + 1
    }
  }
  return counts
})

const unsubNoReasonCount = computed(() =>
  unsubscribes.value.filter(r => !r.reason).length
)

const runUnsubAiClassify = async () => {
  unsubAiLoading.value = true
  unsubAiResult.value = ''
  try {
    const res = await axios.get(`${ADMIN_API}/ai/marketing/unsubscribe/ai_classify`)
    unsubAiResult.value = res.data?.result || '（無內容）'
  } catch (e) {
    window.$toast?.error('AI 分析失敗', e.response?.data?.detail || e.message)
  } finally {
    unsubAiLoading.value = false
  }
}

// A/B tab state
const abCampaigns = ref([])
const abLoading = ref(false)
const abLoaded = ref(false)
const abDeciding = ref({}) // { [groupId]: { loading, variant } }

// AI 洞察 state { [campaignId]: { insightsLoading, insights, insightsError, stepsLoading, steps, stepsError } }
const aiInsights = ref({})
const setAiInsight = (id, patch) => {
  aiInsights.value = { ...aiInsights.value, [id]: { ...(aiInsights.value[id] || {}), ...patch } }
}

const fetchInsights = async (c) => {
  setAiInsight(c.id, { insightsLoading: true, insightsError: null })
  try {
    const res = await axios.get(`${ADMIN_API}/ai/marketing/campaign/${c.id}/ai_insights`)
    setAiInsight(c.id, { insights: res.data?.result || '（無內容）', insightsLoading: false })
  } catch (e) {
    setAiInsight(c.id, { insightsError: e.response?.data?.detail || e.message, insightsLoading: false })
  }
}

const fetchNextSteps = async (c) => {
  setAiInsight(c.id, { stepsLoading: true, stepsError: null })
  try {
    const res = await axios.get(`${ADMIN_API}/ai/marketing/campaign/${c.id}/next_steps`)
    setAiInsight(c.id, { steps: res.data?.result || '（無內容）', stepsLoading: false })
  } catch (e) {
    setAiInsight(c.id, { stepsError: e.response?.data?.detail || e.message, stepsLoading: false })
  }
}

const loadAiInsights = (c) => {
  const state = aiInsights.value[c.id]
  if (!state?.insights && !state?.insightsLoading) fetchInsights(c)
  if (!state?.steps && !state?.stepsLoading) fetchNextSteps(c)
}

// A/B variant generation state
const abVariantStyle = ref({})
const abVariantGenerating = ref({})
const abVariantResult = ref({})
const abVariantError = ref({})

const generateAbVariant = async (group) => {
  const id = group.id
  abVariantGenerating.value = { ...abVariantGenerating.value, [id]: true }
  abVariantError.value = { ...abVariantError.value, [id]: null }
  abVariantResult.value = { ...abVariantResult.value, [id]: null }
  try {
    const res = await axios.post(`${ADMIN_API}/ai/marketing/campaign/${id}/generate_ab_variant`, {
      variant_style: abVariantStyle.value[id] || 'engaging'
    })
    const raw = res.data?.result || ''
    let parsed = null
    try { parsed = JSON.parse(raw) } catch (_) {}
    if (parsed?.subject) {
      abVariantResult.value = { ...abVariantResult.value, [id]: parsed }
    } else {
      abVariantError.value = { ...abVariantError.value, [id]: 'AI 回傳格式異常，請重試' }
    }
  } catch (e) {
    abVariantError.value = { ...abVariantError.value, [id]: e.response?.data?.detail || e.message }
  } finally {
    abVariantGenerating.value = { ...abVariantGenerating.value, [id]: false }
  }
}

const copyAbVariantSubject = (id) => {
  const r = abVariantResult.value[id]
  if (r?.subject) navigator.clipboard.writeText(r.subject).then(() => window.$toast?.success('已複製主旨'))
}
const copyAbVariantBody = (id) => {
  const r = abVariantResult.value[id]
  if (r?.body_html) navigator.clipboard.writeText(r.body_html).then(() => window.$toast?.success('已複製 HTML 內文'))
}

// Scheduled tab state
const scheduledCampaigns = ref([])
const scheduledLoading = ref(false)

// Journey stats tab state
const journeyLoading = ref(false)

// Expand rows state (inline open/click list)
const expandedRows = ref({}) // { [id]: { loading, data, error, tab: 'open'|'click', open } }

// Always replace the whole object so Vue 3 reactivity triggers reliably
const setExpandRow = (id, patch) => {
  expandedRows.value = { ...expandedRows.value, [id]: { ...(expandedRows.value[id] || {}), ...patch } }
}

const toggleExpand = async (c, tab = 'open') => {
  const id = c.id
  const row = expandedRows.value[id]
  if (row) {
    if (row.open && row.tab === tab) { setExpandRow(id, { open: false }); return }
    setExpandRow(id, { open: true, tab })
    return
  }
  setExpandRow(id, { loading: true, data: null, error: null, tab, open: true })
  try {
    const res = await axios.get(`${ADMIN_API}/ai/marketing/campaign/${id}`)
    setExpandRow(id, { data: res.data, loading: false })
  } catch (e) {
    setExpandRow(id, { error: e.response?.data?.detail || e.message, loading: false })
  }
}

const downloadCsv = (rows, filename) => {
  const bom = '﻿'
  const content = bom + rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n')
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.click()
  URL.revokeObjectURL(url)
}

const exportCampaignCsv = (c, recipientList) => {
  const rows = [
    ['Email', '姓名', '狀態', '是否開信', '是否點擊', '寄出時間'],
    ...recipientList.map(r => [
      r.email, r.name || '', r.status || '',
      r.is_opened ? '是' : '否',
      r.is_clicked ? '是' : '否',
      r.sent_at ? fmtDateTime(r.sent_at) : ''
    ])
  ]
  downloadCsv(rows, `campaign_${c.id}_recipients.csv`)
}

const exportOverviewCsv = () => {
  const rows = [
    ['ID', '主旨', '狀態', '寄出數', '開信數', '點擊數', '開信率', '點擊率', '退訂數', '建立時間'],
    ...filteredCampaigns.value.map(c => [
      c.id, c.subject || '', statusLabel(c.status),
      c.total_recipients || 0, c.open_count || 0, c.click_count || 0,
      pct(openRate(c)), pct(clickRate(c)),
      unsubCount(c.id), fmtDate(c.created_at)
    ])
  ]
  downloadCsv(rows, 'marketing_overview.csv')
}

// Tracking report tab state
const trackingLoading = ref(false)
const trackingEvents = ref([])
const trackingCampaigns = ref([])
const trackingFilter = ref({ campaign_id: '', event_type: '', contact_email: '', since: '' })
const trackingSummary = computed(() => {
  const evs = trackingEvents.value
  return {
    opens: evs.filter(e => e.type === 'open').length,
    clicks: evs.filter(e => e.type === 'click').length,
    uniqueRecipients: new Set(evs.map(e => e.recipient || e.email).filter(Boolean)).size,
    formSubmissions: evs.filter(e => e.type === 'form_submission').length,
  }
})
const filteredTrackingEvents = computed(() => {
  const et = trackingFilter.value.event_type
  if (!et) return trackingEvents.value
  return trackingEvents.value.filter(e => e.type === et)
})
const journeys = computed(() => (aiMarketingState.value?.journeys || []))
const avgJourneyOpenRate = computed(() => {
  const js = journeys.value.filter(j => (j._stats?.enrollments_total || 0) > 0)
  return js.length ? js.reduce((s, j) => s + (j._stats?.open_rate_step1 || 0), 0) / js.length : 0
})
const journeyStatusLabel = (s) => ({ active: '進行中', paused: '已暫停', draft: '草稿', completed: '已完成' }[s] || s || '—')

const showDetail = ref(false)
const selectedCampaign = ref(null)
const detail = ref(null)
const detailLoading = ref(false)
const detailError = ref('')

// --- Helpers ---
const openRate = (c) => {
  const t = c?.total_recipients || 0
  return t > 0 ? (c?.open_count || 0) / t : 0
}
const clickRate = (c) => {
  const t = c?.total_recipients || 0
  return t > 0 ? (c?.click_count || 0) / t : 0
}
const openRatePct = (c) => Math.min(100, openRate(c) * 100)
const clickRatePct = (c) => Math.min(100, clickRate(c) * 100)
const pct = (v) => (v * 100).toFixed(1) + '%'
const rateColor = (v) => {
  if (v >= 0.25) return 'var(--color-success)'
  if (v >= 0.10) return 'var(--color-warning)'
  return 'var(--color-danger)'
}

const fmtDate = (ts) => {
  if (!ts) return '—'
  return new Date(ts).toLocaleDateString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' })
}
const fmtDateTime = (ts) => {
  if (!ts) return '—'
  return new Date(ts).toLocaleString('zh-TW', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
}

const statusLabel = (s) => ({ sent: '已完成', sending: '寄送中', failed: '失敗', ab_testing: 'A/B 測試', pending: '等待中' }[s] || s)
const statusBadge = (s) => ({ sent: 'badge-success', sending: 'badge-info', failed: 'badge-danger', ab_testing: 'badge-warning', pending: '' }[s] || '')
const recipientStatusLabel = (s) => ({ sent: '已寄出', failed: '失敗', pending: '等待', moved: '已轉移' }[s] || s)
const recipientStatusBadge = (s) => ({ sent: 'badge-success', failed: 'badge-danger', pending: '', moved: 'badge-info' }[s] || '')

const unsubCount = (id) => unsubscribes.value.filter(u => u.campaign_id === id).length

const metricLabel = (m) => ({ open_rate: '開信率', click_rate: '點擊率', ctor: 'CTOR（點擊/開信）' }[m] || m || '—')

// A/B bar width — relative to the higher of the two metrics
const abBarWidth = (group, variant) => {
  if (!group.ab) return 0
  const a = group.ab.a_metric_pct || 0
  const b = group.ab.b_metric_pct || 0
  const max = Math.max(a, b, 0.01)
  const val = variant === 'A' ? a : b
  return Math.round((val / max) * 100)
}

// --- Summary ---
const summary = computed(() => {
  const cs = campaigns.value
  const totalCampaigns = cs.length
  const totalSent = cs.reduce((a, c) => a + (c.total_recipients || 0), 0)
  const withSent = cs.filter(c => (c.total_recipients || 0) > 0)
  const avgOpenRate = withSent.length ? withSent.reduce((a, c) => a + openRate(c), 0) / withSent.length : 0
  const avgClickRate = withSent.length ? withSent.reduce((a, c) => a + clickRate(c), 0) / withSent.length : 0
  return { totalCampaigns, totalSent, avgOpenRate, avgClickRate }
})

// --- Filter ---
const filteredCampaigns = computed(() => {
  const q = filterQ.value.toLowerCase().trim()
  const st = filterStatus.value
  return campaigns.value.filter(c => {
    if (st && c.status !== st) return false
    if (q) {
      const hay = `#${c.id} ${c.subject || ''} ${c.sender || ''}`.toLowerCase()
      if (!hay.includes(q)) return false
    }
    return true
  })
})

// --- Timeline chart (group by day, count events) ---
const timelineChart = computed(() => {
  if (!detail.value?.chart_data?.length) return []
  const logs = detail.value.chart_data
  const dayMap = {}
  for (const log of logs) {
    const day = (log.timestamp || '').slice(0, 10)
    if (!day) continue
    if (!dayMap[day]) dayMap[day] = { opens: 0, clicks: 0 }
    if (log.event_type === 'open') dayMap[day].opens++
    else if (log.event_type === 'click') dayMap[day].clicks++
  }
  const days = Object.keys(dayMap).sort()
  const maxTotal = Math.max(...days.map(d => dayMap[d].opens + dayMap[d].clicks), 1)
  return days.map(d => ({
    height: (dayMap[d].opens + dayMap[d].clicks) / maxTotal,
    hasClick: dayMap[d].clicks > 0,
    label: `${d}: 開信 ${dayMap[d].opens}, 點擊 ${dayMap[d].clicks}`
  }))
})

// --- Data loading ---
const loadAll = async () => {
  loading.value = true
  offset.value = 0
  campaigns.value = []
  try {
    const [camRes, unsubRes] = await Promise.all([
      axios.get(`${ADMIN_API}/ai/marketing/campaigns`, { params: { skip: 0, limit: pageLimit } }),
      axios.post(`${ADMIN_API}/ai/marketing/unsubscribe/list`)
    ])
    campaigns.value = Array.isArray(camRes.data) ? camRes.data : []
    unsubscribes.value = Array.isArray(unsubRes.data) ? unsubRes.data : []
    offset.value = campaigns.value.length
  } catch (e) {
    console.error('MarketingReport load error', e)
    window.$toast?.error('載入失敗', '無法載入行銷活動資料')
  } finally {
    loading.value = false
  }
}

const loadMore = async () => {
  if (loadingMore.value) return
  loadingMore.value = true
  try {
    const res = await axios.get(`${ADMIN_API}/ai/marketing/campaigns`, {
      params: { skip: offset.value, limit: pageLimit }
    })
    const items = Array.isArray(res.data) ? res.data : []
    campaigns.value = campaigns.value.concat(items)
    offset.value += items.length
  } catch (e) {
    console.error(e)
    window.$toast?.error('載入失敗', '無法載入更多活動')
  } finally {
    loadingMore.value = false
  }
}

const openDetail = async (c) => {
  selectedCampaign.value = c
  showDetail.value = true
  detail.value = null
  detailError.value = ''
  detailLoading.value = true
  try {
    const res = await axios.get(`${ADMIN_API}/ai/marketing/campaign/${c.id}`)
    detail.value = res.data
  } catch (e) {
    detailError.value = e.response?.data?.detail || e.message || '載入失敗'
  } finally {
    detailLoading.value = false
  }
}

const loadAbCampaigns = async () => {
  if (abLoading.value) return
  abLoading.value = true
  try {
    const res = await axios.get(`${ADMIN_API}/ai/marketing/campaigns`, {
      params: { skip: 0, limit: 50, ab_grouped: true }
    })
    abCampaigns.value = (Array.isArray(res.data) ? res.data : []).filter(c => c.ab !== null && c.ab !== undefined)
    abLoaded.value = true
  } catch (e) {
    window.$toast?.error('載入失敗', '無法載入 A/B 測試活動')
  } finally {
    abLoading.value = false
  }
}

const forceDecideAb = async (groupId, variant) => {
  abDeciding.value[groupId] = { loading: true, variant }
  try {
    const body = variant ? { winner_variant: variant } : {}
    await axios.post(`${ADMIN_API}/ai/marketing/ab/decide/${groupId}`, body)
    const msg = variant
      ? `已強制指定變體 ${variant} 勝出，正在發送給剩餘收件人...`
      : '已提交自動決定，系統正在評估結果...'
    window.$toast?.success('決策已提交', msg)
    setTimeout(() => loadAbCampaigns(), 3000)
  } catch (e) {
    window.$toast?.error('操作失敗', e.response?.data?.detail || e.message || '請稍後再試')
  } finally {
    if (abDeciding.value[groupId]) abDeciding.value[groupId].loading = false
  }
}

const loadScheduled = async () => {
  scheduledLoading.value = true
  try {
    const res = await axios.get(`${ADMIN_API}/ai/marketing/campaigns`, {
      params: { skip: 0, limit: 100 }
    })
    scheduledCampaigns.value = (Array.isArray(res.data) ? res.data : []).filter(c => c.status === 'scheduled')
  } catch (e) {
    console.error(e)
    window.$toast?.error('載入失敗', '無法載入排程活動')
  } finally {
    scheduledLoading.value = false
  }
}

const cancelScheduled = async (c) => {
  if (!confirm(`確定取消排程「${c.subject}」？`)) return
  try {
    await axios.delete(`${ADMIN_API}/ai/marketing/campaign/${c.id}/schedule`)
    scheduledCampaigns.value = scheduledCampaigns.value.filter(x => x.id !== c.id)
    window.$toast?.success('已取消排程', c.subject)
  } catch (e) {
    window.$toast?.error('取消失敗', e.response?.data?.detail || e.message)
  }
}

function countdown(dt) {
  if (!dt) return '—'
  const diff = new Date(dt) - new Date()
  if (diff <= 0) return '即將發送'
  const h = Math.floor(diff / 3600000)
  const m = Math.floor((diff % 3600000) / 60000)
  if (h > 24) return `${Math.floor(h/24)} 天後`
  if (h > 0) return `${h} 小時 ${m} 分後`
  return `${m} 分鐘後`
}

const loadJourneyStats = async () => {
  if (journeyLoading.value) return
  journeyLoading.value = true
  try {
    await store.fetchJourneys()
  } catch (e) {
    console.error('journey stats load error', e)
    window.$toast?.error('載入失敗', '無法載入旅程統計')
  } finally {
    journeyLoading.value = false
  }
}

const loadTrackingEvents = async () => {
  if (trackingLoading.value) return
  trackingLoading.value = true
  try {
    // Load campaign list for filter dropdown if not yet loaded
    if (!trackingCampaigns.value.length) {
      const cr = await axios.get(`${ADMIN_API}/ai/marketing/campaigns`, { params: { skip: 0, limit: 100 } })
      trackingCampaigns.value = Array.isArray(cr.data) ? cr.data : []
    }
    const params = { limit: 200 }
    if (trackingFilter.value.campaign_id) params.campaign_id = trackingFilter.value.campaign_id
    if (trackingFilter.value.contact_email) params.contact_email = trackingFilter.value.contact_email
    if (trackingFilter.value.since) params.since = trackingFilter.value.since + 'T00:00:00'
    const res = await axios.get(`${ADMIN_API}/ai/marketing/analysis/events`, { params })
    trackingEvents.value = Array.isArray(res.data?.events) ? res.data.events : (Array.isArray(res.data) ? res.data : [])
  } catch (e) {
    console.error('tracking events load error', e)
    window.$toast?.error('載入失敗', e.response?.data?.detail || '無法載入追蹤事件')
    trackingEvents.value = []
  } finally {
    trackingLoading.value = false
  }
}

onMounted(() => {
  loadAll()
  loadScheduled()
})

// ── 競爭者 EDM 分析 ──
const competitorSubject = ref('')
const competitorHtml = ref('')
const competitorText = ref('')
const competitorLoading = ref(false)
const competitorResult = ref(null)
const competitorError = ref('')
const runCompetitorAnalysis = async () => {
  if (!competitorSubject.value.trim()) { competitorError.value = '請輸入競爭者郵件主旨'; return }
  competitorLoading.value = true
  competitorResult.value = null
  competitorError.value = ''
  try {
    const res = await axios.post(`${ADMIN_API}/ai/marketing/competitor_analysis`, {
      subject: competitorSubject.value,
      body_html: competitorHtml.value,
      body_text: competitorText.value,
    }, { headers: { Authorization: `Bearer ${store.token}` } })
    competitorResult.value = res.data
  } catch (e) {
    competitorError.value = e.response?.data?.detail || e.message
  } finally {
    competitorLoading.value = false
  }
}

// ── 月度績效報告 ──
const now = new Date()
const reportYear = ref(now.getFullYear())
const reportMonth = ref(now.getMonth() + 1)
const reportYearOptions = [now.getFullYear() - 1, now.getFullYear()]
const monthlyReportLoading = ref(false)
const monthlyReportResult = ref(null)
const monthlyReportError = ref('')
const runMonthlyReport = async () => {
  monthlyReportLoading.value = true
  monthlyReportResult.value = null
  monthlyReportError.value = ''
  try {
    const res = await axios.get(`${ADMIN_API}/ai/marketing/monthly_report`, {
      params: { year: reportYear.value, month: reportMonth.value },
      headers: { Authorization: `Bearer ${store.token}` },
    })
    monthlyReportResult.value = res.data
  } catch (e) {
    monthlyReportError.value = e.response?.data?.detail || e.message
  } finally {
    monthlyReportLoading.value = false
  }
}
</script>
